import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithPhone: (phone: string, otp: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateToken: (token: string) => void;
  setDefaultToken: () => void;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
}

interface TokenPayload {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  exp: number;
  iat: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("customer-token");

    // Default token for testing if no token exists
    const defaultToken =
      "eyJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoiMjE3NTdmMTItZDUwMy00YjQ1LTllMzQtMDdmZGM3ZTg1NTk1IiwiZXhwIjoxNzU5ODM5NDI3fQ.mSMFbQVk4wx1kBIIpHjEl3Ryz-Mz3MF-4FJoLNbe57E";

    const tokenToUse = storedToken || defaultToken;

    if (tokenToUse) {
      try {
        const decoded = jwtDecode<TokenPayload>(tokenToUse);
        const currentTime = Date.now() / 1000;

        if (decoded.exp > currentTime) {
          setToken(tokenToUse);
          setUser({
            id: decoded.id,
            email: decoded.email,
            firstName: decoded.firstName,
            lastName: decoded.lastName,
          });

          // Store the token if it's the default one
          if (!storedToken) {
            localStorage.setItem("customer-token", tokenToUse);
          }
        } else {
          // Token expired, clear it
          localStorage.removeItem("customer-token");
        }
      } catch (error) {
        console.error("Invalid token:", error);
        localStorage.removeItem("customer-token");
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL ||
          "https://api-staging.withsplice.com"
        }/api/v1/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Login failed");
      }

      const data = await response.json();
      const { token: authToken, user: userData } = data;

      localStorage.setItem("customer-token", authToken);
      setToken(authToken);
      setUser(userData);
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const loginWithPhone = async (phone: string, otp: string) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL ||
          "https://api-staging.withsplice.com"
        }/api/v1/auth/verify-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone, otp }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "OTP verification failed");
      }

      const data = await response.json();
      const { token: authToken, user: userData } = data;

      localStorage.setItem("customer-token", authToken);
      setToken(authToken);
      setUser(userData);
    } catch (error) {
      console.error("Phone login error:", error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL ||
          "https://api-staging.withsplice.com"
        }/api/v1/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Registration failed");
      }

      const result = await response.json();
      const { token: authToken, user: userData } = result;

      localStorage.setItem("customer-token", authToken);
      setToken(authToken);
      setUser(userData);
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("customer-token");
    setToken(null);
    setUser(null);
    navigate("/");
  };

  const updateToken = (newToken: string) => {
    localStorage.setItem("customer-token", newToken);
    setToken(newToken);

    try {
      const decoded = jwtDecode<TokenPayload>(newToken);
      setUser({
        id: decoded.id,
        email: decoded.email,
        firstName: decoded.firstName,
        lastName: decoded.lastName,
      });
    } catch (error) {
      console.error("Failed to decode token:", error);
    }
  };

  const setDefaultToken = () => {
    const defaultToken =
      "eyJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoiMjE3NTdmMTItZDUwMy00YjQ1LTllMzQtMDdmZGM3ZTg1NTk1IiwiZXhwIjoxNzU5ODM5NDI3fQ.mSMFbQVk4wx1kBIIpHjEl3Ryz-Mz3MF-4FJoLNbe57E";
    updateToken(defaultToken);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        loginWithPhone,
        register,
        logout,
        updateToken,
        setDefaultToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
